import { useEffect, useRef, useState } from 'react';
import { motionAllowed } from '../lib/motion';

const COUNT_MS = 700;
// Quick at first, then settling gently onto the new value.
const easeOut = (progress: number) => 1 - (1 - progress) ** 3;

/**
 * A whole number that counts its way to its value: up from nothing as it first appears, then on
 * to each new value, as credits do once spent, rather than changing in one step. A change
 * mid-count carries on from where the count had got to.
 */
export function useCountingNumber(value: number): number {
  const [first] = useState(() => (motionAllowed() ? 0 : value));
  const [shown, setShown] = useState(first);
  const current = useRef(first);

  useEffect(() => {
    const start = current.current;
    if (start === value || !motionAllowed()) {
      current.current = value;
      return;
    }
    const began = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const progress = Math.min(1, (now - began) / COUNT_MS);
      current.current = Math.round(start + (value - start) * easeOut(progress));
      setShown(current.current);
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [value]);

  return motionAllowed() ? shown : value;
}
